import { useGetRevenueExportMutation } from 'app/apis/queryApi';
import { RootState } from 'app/store';
import ActionsButton from 'common/components/buttons/actionsButton';
import ExportButton from 'common/components/buttons/exportButton';
import { closeModal, confirmModal, openModal } from 'features/modal/modalSlice';
import { selectPaginationOptions } from 'features/pagination/paginationOptionsSlice';
import {
  BaseSelectedRow,
  selectRowsByComponentId,
  selectRowSelectionsByComponentId
} from 'features/row-selection/rowSelectionSlice';

import SearchBar from 'features/search-bar/searchBar';
import {
  applySearchToTableAndGraph,
  clearSearch,
  getSearchTermsById
} from 'features/search-bar/searchBarSlice';
import { FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MappedRevenueExportRequest } from './types/MappedRevenueExportRequest';
import { selectCurrencyCode } from 'features/settings/settingsSlice';
import { ViewType } from 'features/data-view-selection/view-type';
import { toggleSelection } from 'features/data-view-selection/dataViewSelectionSlice';
import { selectDateRangeLocalIso } from 'features/date-range-selection/dateRangeSlice';
import {
  DeleteRevenueRequest,
  useDeleteMultipleRevenuesMutation
} from 'app/apis/commandApi';
import { addListener } from '@reduxjs/toolkit';
import { ButtonTheme } from 'common/components/buttons/button-theme';

const RevenueSearchInteractionBar: FC<{
  componentId: string;
  currentViewType: ViewType;
}> = ({
  componentId,
  currentViewType
}: {
  componentId: string;
  currentViewType: ViewType;
}) => {
  const dispatch = useDispatch();

  const { tableValueToSearch } = useSelector((state: RootState) =>
    getSearchTermsById(state, componentId)
  );

  const rows = useSelector((state: RootState) =>
    selectRowsByComponentId(state, componentId)
  );

  const { siteId } = useSelector((state: RootState) => state.selectedSite);
  const { startDate, endDate } = useSelector(selectDateRangeLocalIso);

  const paginationOptions = useSelector((state: RootState) =>
    selectPaginationOptions(state, componentId)
  );

  const selectedCurrencyCode = useSelector(selectCurrencyCode);

  const request: MappedRevenueExportRequest = {
    siteId,
    startDate,
    endDate,
    currencyCode: selectedCurrencyCode,
    searchTerm: paginationOptions?.searchTerm ?? '',
    viewType: currentViewType
  };

  const [triggerDownload, { isLoading: isDownloadingCsv }] =
    useGetRevenueExportMutation();

  const toggleViewType = (viewType: ViewType) => {
    dispatch(
      toggleSelection({
        newSelection: viewType,
        componentId
      })
    );
  };

  const [deleteMultipleRevenues] = useDeleteMultipleRevenuesMutation();

  const onDeleteMultipleRevenues = async (rowsToDelete: any) => {
    const deleteRevenueRequest = rowsToDelete.map(
      (row: BaseSelectedRow): DeleteRevenueRequest => {
        const request: DeleteRevenueRequest = {
          revenueId: row.rowId,
          revenueClosedDateTime: row.rowData
        };

        return request;
      }
    );

    try {
      await deleteMultipleRevenues({
        siteId,
        revenue: deleteRevenueRequest,
        componentId
      }).unwrap();
    } catch (error) {
      console.error('Failed to delete revenue:', error);
    }
  };

  return (
    <>
      <div className="flex flex-row align-middle justify-between flex-wrap py-2 w-full">
        <SearchBar
          searchValue={tableValueToSearch}
          onClickFunction={(searchTerm: string) => {
            dispatch(
              applySearchToTableAndGraph({
                id: componentId,
                valueToSearch: searchTerm
              })
            );
          }}
          onClearFunction={() => {
            dispatch(
              clearSearch({
                id: componentId
              })
            );
          }}
        />
        <div className="flex align-middle">
          <ExportButton
            onExport={(): any =>
              isDownloadingCsv ? null : triggerDownload(request)
            }
            isLoading={isDownloadingCsv}
          />
          <button
            className={`border rounded border-off-navy ml-3 text-sm py-2.5 px-5 text-center ${
              currentViewType === ViewType.Simple
                ? 'bg-navy text-white'
                : 'bg-white text-black'
            } `}
            onClick={() => toggleViewType(ViewType.Simple)}
          >
            Simple
          </button>

          <button
            className={`border rounded border-off-navy ml-3 text-sm py-2.5 px-5 text-center ${
              currentViewType === ViewType.Detailed
                ? 'bg-navy text-white'
                : 'bg-white text-black'
            } `}
            onClick={() => toggleViewType(ViewType.Detailed)}
          >
            Detailed
          </button>

          <div className="ml-3">
            <ActionsButton
              label="Actions"
              actions={[
                {
                  label: 'Delete Selected Rows',
                  key: 'delete',
                  isActive: false,
                  onAction: (): any => {
                    if (rows.length === 0) {
                      return;
                    }

                    dispatch(
                      addListener({
                        actionCreator: confirmModal,
                        effect: async (action, listenerApi) => {
                          listenerApi.cancelActiveListeners();

                          // need to get the rows from the state here. So we can get the rows while the Listener Api is still active
                          const state = listenerApi.getState() as RootState;

                          const rowsToDelete = selectRowsByComponentId(
                            state,
                            componentId
                          );

                          await onDeleteMultipleRevenues(rowsToDelete);

                          listenerApi.dispatch(closeModal());

                          listenerApi.unsubscribe();
                        }
                      })
                    );

                    dispatch(
                      openModal({
                        modalType: 'deleteRevenue',
                        modalProps: {
                          title: 'Delete Revenue',
                          content: `Are you sure you want to delete these records? Right now you are deleting ${rows.length} rows. This action cannot be undone.`,
                          confirmButtonText: 'Delete',
                          cancelButtonText: 'Cancel',
                          confirmButtonTheme: ButtonTheme.PRIMARY,
                          cancelButtonTheme: ButtonTheme.CANCEL,
                          componentId
                        }
                      })
                    );
                  },
                  isLoading: false
                }
              ]}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default RevenueSearchInteractionBar;
